/**
 * SkyPlus Case Management — Shared Models
 *
 * Domain: legal case lifecycle tracking for insurance recovery, subrogation,
 * defense, loan default litigation, and general legal disputes.
 *
 * These interfaces drive the dashboard, cases list, case detail, and the
 * new-case wizard. They mirror the shape we'd expect from a Spring Boot
 * backend (sp_legal_case, sp_legal_event, sp_legal_party, etc.) so the
 * service can be swapped from mock data → real HTTP with no component change.
 */

// ── Enums (string unions for narrow Angular template binding) ───────────────

export type CaseType =
  | 'SUBROGATION'       // Insurer recovering from at-fault third party
  | 'DEFENSE'           // Defending insured / company being sued
  | 'RECOVERY'          // Recovering owed money (loan default, unpaid premium)
  | 'FRAUD'             // Suspected fraudulent claim — civil/criminal pursuit
  | 'EMPLOYMENT'        // Wrongful termination, discrimination, harassment
  | 'CONTRACT'          // Breach of contract dispute
  | 'REGULATORY'        // IRA / CMA / SACCO regulator inquiry
  | 'OTHER';

export type CaseStatus =
  | 'ACTIVE'            // Currently being worked
  | 'PENDING'           // Awaiting external input (court, opposing counsel)
  | 'ON_HOLD'           // Suspended by client decision
  | 'CLOSED';           // Disposed of (any outcome)

export type CasePriority = 'HIGH' | 'MEDIUM' | 'LOW';

export type CaseOutcome =
  | 'WON'
  | 'LOST'
  | 'SETTLED'
  | 'WITHDRAWN'
  | 'DISMISSED'
  | 'TIME_BARRED';

export type OurRole = 'PLAINTIFF' | 'DEFENDANT';

export type PartyRole =
  | 'PLAINTIFF'
  | 'DEFENDANT'
  | 'WITNESS'
  | 'COUNSEL_PLAINTIFF'
  | 'COUNSEL_DEFENDANT'
  | 'JUDGE'
  | 'EXPERT'
  | 'OTHER';

export type PartyType = 'INDIVIDUAL' | 'COMPANY' | 'GOVERNMENT';

export type DocumentType =
  | 'SUMMONS'
  | 'COURT_ORDER'
  | 'WITNESS_STATEMENT'
  | 'CONTRACT'
  | 'CLAIM_FORM'
  | 'DEMAND_LETTER'
  | 'JUDGMENT'
  | 'SETTLEMENT_AGREEMENT'
  | 'CORRESPONDENCE'
  | 'EVIDENCE_PHOTO'
  | 'EVIDENCE_REPORT'
  | 'POLICE_REPORT'
  | 'MEDICAL_REPORT'
  | 'INVOICE'
  | 'OTHER';

export type EventType =
  | 'HEARING'
  | 'MENTION'
  | 'FILING_DEADLINE'
  | 'MEDIATION'
  | 'SETTLEMENT_MEETING'
  | 'WITNESS_PREP'
  | 'COURT_VISIT'
  | 'PAYMENT_DUE'
  | 'OTHER';

export type EventStatus = 'UPCOMING' | 'COMPLETED' | 'POSTPONED' | 'MISSED';

export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'DONE' | 'OVERDUE';

export type ActivityCategory =
  | 'STATUS'
  | 'DOCUMENT'
  | 'EVENT'
  | 'TASK'
  | 'PARTY'
  | 'NOTE'
  | 'COST'
  | 'OPENED'
  | 'CLOSED';

export type CostType =
  | 'COURT_FEE'
  | 'LEGAL_FEE'
  | 'EXPERT_FEE'
  | 'DISBURSEMENT'
  | 'SETTLEMENT_PAID'
  | 'RECOVERY_RECEIVED'
  | 'OTHER';

// ── Core entities ───────────────────────────────────────────────────────────

export interface LegalCase {
  id: string;                        // 'LGL-2026-00012'
  title: string;
  type: CaseType;
  status: CaseStatus;
  priority: CasePriority;

  // Source linkage — where the case originated
  sourceRef?: string;                // 'CLM-2025-08-1234' (claim number)
  sourceType?: string;               // 'INSURANCE_CLAIM' | 'LOAN_ACCOUNT' | 'CONTRACT'
  sourceDescription?: string;        // 'Motor third-party claim — Mombasa Rd RTA'

  // Financial
  exposureAmount: number;            // KES at stake (claim value or potential liability)
  recoveredAmount?: number;          // KES recovered (subrogation) or paid (defense settlement)
  currency: string;                  // 'KES'

  // Counterparty (summary level; full party list in CaseParty)
  ourRole: OurRole;
  opposingParty: string;             // 'John Mwangi Kariuki'
  opposingCounsel?: string;          // 'Karanja & Co Advocates'

  // Our team
  assignedTo: string;                // 'Susan Wairimu, Senior Legal Officer'
  assignedDepartment?: string;       // 'Legal & Recovery'
  externalCounsel?: string;          // Outsourced firm

  // Court
  court?: string;                    // 'Milimani Commercial Magistrates Court'
  courtCaseNumber?: string;          // 'MCC NO. 1234 OF 2026'
  judge?: string;

  // Dates
  openedDate: Date;
  expectedCloseDate?: Date;
  closedDate?: Date;
  nextEventDate?: Date;              // Computed — drives urgency display

  // Computed counts (rendered as badges)
  documentCount: number;
  eventCount: number;
  pendingTaskCount: number;
  partyCount: number;

  // Disposition (when status === 'CLOSED')
  outcome?: CaseOutcome;
  outcomeNote?: string;

  // Tags for free-form classification
  tags?: string[];
}

export interface CaseParty {
  id: string;
  caseId: string;
  role: PartyRole;
  type: PartyType;
  name: string;
  idNumber?: string;                 // National ID, PIN, company reg
  contact?: string;
  email?: string;
  address?: string;
  representedBy?: string;            // Counsel name
  notes?: string;
}

export interface CaseDocument {
  id: string;
  caseId: string;
  name: string;
  type: DocumentType;
  uploadedBy: string;
  uploadedAt: Date;
  size: number;
  mimeType: string;
  nodeId?: string;                   // Future: Alfresco node reference
  description?: string;
}

export interface CaseEvent {
  id: string;
  caseId: string;
  title: string;
  type: EventType;
  scheduledAt: Date;
  durationMinutes: number;
  location: string;
  status: EventStatus;
  attendees: string[];
  reminderDaysBefore: number[];      // e.g. [7, 1] — fire 7 and 1 days before
  outcomeNote?: string;
  createdAt: Date;
}

export interface CaseTask {
  id: string;
  caseId: string;
  description: string;
  assignee: string;
  dueDate: Date;
  status: TaskStatus;
  createdAt: Date;
  completedAt?: Date;
  priority?: CasePriority;
}

export interface CaseActivity {
  id: string;
  caseId: string;
  timestamp: Date;
  actor: string;
  action: string;                    // 'Uploaded summons.pdf'
  details?: string;
  category: ActivityCategory;
}

export interface CaseCost {
  id: string;
  caseId: string;
  date: Date;
  description: string;
  type: CostType;
  amount: number;                    // Positive for cost, negative for recovery
  paidTo?: string;
  reference?: string;                // Invoice/receipt number
}

export interface CaseNote {
  id: string;
  caseId: string;
  author: string;
  content: string;
  createdAt: Date;
  isPrivileged?: boolean;            // Attorney-client privileged
}

// ── Dashboard aggregates ────────────────────────────────────────────────────

export interface CaseDashboardStats {
  activeCases: number;
  hearingsThisWeek: number;
  overdueActions: number;
  closedThisMonth: number;
  newThisWeek: number;
  totalExposureKES: number;
  recoveredYTDKES: number;
  recoveryRatePct: number;           // recovered / total settled
}

export interface CaseTypeBreakdown {
  type: CaseType;
  label: string;
  count: number;
  exposureKES: number;
}

export interface CaseStatusBreakdown {
  status: CaseStatus;
  label: string;
  count: number;
  color: string;
}

// ── Filter / list state ─────────────────────────────────────────────────────

export interface CaseListFilter {
  searchTerm?: string;
  status?: CaseStatus | 'ALL';
  type?: CaseType | 'ALL';
  priority?: CasePriority | 'ALL';
  assignedTo?: string | 'ALL';
  hasUpcomingEvents?: boolean;
  overdueOnly?: boolean;
}

// ── New-case wizard request ─────────────────────────────────────────────────

export interface NewCaseRequest {
  title: string;
  type: CaseType;
  priority: CasePriority;
  ourRole: OurRole;
  sourceRef?: string;
  sourceType?: string;
  sourceDescription?: string;
  exposureAmount: number;
  opposingParty: string;
  opposingCounsel?: string;
  assignedTo: string;
  court?: string;
  courtCaseNumber?: string;
  expectedCloseDate?: Date;
  parties?: Partial<CaseParty>[];
  firstEvent?: Partial<CaseEvent>;
  notes?: string;
}

// ── Helper: display labels (avoid repeating in templates) ───────────────────

export const CASE_TYPE_LABELS: Record<CaseType, string> = {
  SUBROGATION: 'Subrogation Recovery',
  DEFENSE:     'Defense of Insured',
  RECOVERY:    'Debt Recovery',
  FRAUD:       'Fraud Investigation',
  EMPLOYMENT:  'Employment Dispute',
  CONTRACT:    'Contract Dispute',
  REGULATORY:  'Regulatory Inquiry',
  OTHER:       'Other Legal Matter'
};

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  ACTIVE:  'Active',
  PENDING: 'Pending',
  ON_HOLD: 'On Hold',
  CLOSED:  'Closed'
};

export const CASE_STATUS_COLORS: Record<CaseStatus, string> = {
  ACTIVE:  '#4A90D9',
  PENDING: '#ffc107',
  ON_HOLD: '#6c757d',
  CLOSED:  '#28a745'
};

export const CASE_PRIORITY_COLORS: Record<CasePriority, string> = {
  HIGH:   '#dc3545',
  MEDIUM: '#ffc107',
  LOW:    '#28a745'
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  HEARING:            'Court Hearing',
  MENTION:            'Mention',
  FILING_DEADLINE:    'Filing Deadline',
  MEDIATION:          'Mediation',
  SETTLEMENT_MEETING: 'Settlement Meeting',
  WITNESS_PREP:       'Witness Preparation',
  COURT_VISIT:        'Court Visit',
  PAYMENT_DUE:        'Payment Due',
  OTHER:              'Other Event'
};

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  SUMMONS:              'Summons',
  COURT_ORDER:          'Court Order',
  WITNESS_STATEMENT:    'Witness Statement',
  CONTRACT:             'Contract',
  CLAIM_FORM:           'Claim Form',
  DEMAND_LETTER:        'Demand Letter',
  JUDGMENT:             'Judgment',
  SETTLEMENT_AGREEMENT: 'Settlement Agreement',
  CORRESPONDENCE:       'Correspondence',
  EVIDENCE_PHOTO:       'Evidence Photo',
  EVIDENCE_REPORT:      'Evidence Report',
  POLICE_REPORT:        'Police Report',
  MEDICAL_REPORT:       'Medical Report',
  INVOICE:              'Invoice',
  OTHER:                'Other Document'
};

export const DOCUMENT_TYPE_ICONS: Record<DocumentType, string> = {
  SUMMONS:              'gavel',
  COURT_ORDER:          'verified',
  WITNESS_STATEMENT:    'record_voice_over',
  CONTRACT:             'description',
  CLAIM_FORM:           'assignment',
  DEMAND_LETTER:        'mark_email_unread',
  JUDGMENT:             'fact_check',
  SETTLEMENT_AGREEMENT: 'handshake',
  CORRESPONDENCE:       'forward_to_inbox',
  EVIDENCE_PHOTO:       'photo_camera',
  EVIDENCE_REPORT:      'science',
  POLICE_REPORT:        'local_police',
  MEDICAL_REPORT:       'medical_services',
  INVOICE:              'receipt_long',
  OTHER:                'insert_drive_file'
};

export const EVENT_TYPE_ICONS: Record<EventType, string> = {
  HEARING:            'gavel',
  MENTION:            'event_note',
  FILING_DEADLINE:    'schedule_send',
  MEDIATION:          'handshake',
  SETTLEMENT_MEETING: 'forum',
  WITNESS_PREP:       'record_voice_over',
  COURT_VISIT:        'account_balance',
  PAYMENT_DUE:        'payments',
  OTHER:              'event'
};
